import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import api from "./axiosConfig";
import DashboardHeader from "./DashboardHeader";
import ReportFooter from "./ReportFooter";
import Modal from "react-modal";
import {
  ChevronDown,
  Sparkles,
  Radio,
  Eye,
  MessageSquare,
  Clock,
  Target,
  Gift,
  Star,
  ChevronRight,
  X,
  ExternalLink,
  Megaphone,
  Activity,
  Users,
  Calendar,
  Building,
  TrendingUp,
  Mail,
  CheckCircle,
  Search,
  ArrowRight,
  Zap,
  Shield,
  FileText,
} from "lucide-react";
import TopBanner from "./TopBanner";
import EnhancedHeader from "./EnhancedHeader";

const CustomModal = ({ isOpen, onRequestClose, content }) => {
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      className="fixed inset-0 z-50 flex items-center justify-center"
      overlayClassName="fixed inset-0 bg-black bg-opacity-50 z-40"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.3 }}
        className="bg-white rounded-2xl shadow-xl max-w-lg w-full mx-4 p-6"
      >
        <h3 className="text-xl font-semibold text-gray-900 mb-4">Full Input</h3>
        <div className="max-h-96 overflow-y-auto p-4 bg-gray-50 rounded-lg mb-4">
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{content}</p>
        </div>
        <div className="flex justify-end space-x-3">
          <button
            onClick={handleCopy}
            className={`px-4 py-2 rounded-lg text-white font-medium transition-colors ${
              isCopied ? "bg-green-600" : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {isCopied ? "Copied!" : "Copy Full Text"}
          </button>
          <button
            onClick={onRequestClose}
            className="px-4 py-2 bg-gray-200 rounded-lg text-gray-700 font-medium hover:bg-gray-300 transition-colors"
          >
            Close
          </button>
        </div>
      </motion.div>
    </Modal>
  );
};

export default function ReportDashboard() {
  const location = useLocation();
  const [businessIdea, setBusinessIdea] = useState("");
  const [language, setLanguage] = useState("english");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [latestFullReports, setLatestFullReports] = useState([]);
  const [isFetchingReports, setIsFetchingReports] = useState(false);
  const [message, setMessage] = useState("");
  const [showRechargeLink, setShowRechargeLink] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showBoothForm, setShowBoothForm] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showBusinessMatchModal, setShowBusinessMatchModal] = useState(false);

  useEffect(() => {
    // Check if there's data from the Ideation Dashboard
    // const storedInput = localStorage.getItem("businessPlanInput");
    // if (storedInput) {
    //   setBusinessIdea(storedInput);
    //   // Optional: Clear after loading
    //   localStorage.removeItem("businessPlanInput");
    // }

    // Check if there's data from the Ideation Dashboard
    const storedInput = localStorage.getItem("businessPlanInput");
    if (storedInput) {
      setBusinessIdea(storedInput);
      localStorage.removeItem("businessPlanInput");

      // Scroll to and focus the textarea
      const textarea = document.getElementById("businessIdea");
      if (textarea) {
        textarea.scrollIntoView({ behavior: "smooth", block: "center" });
        textarea.focus();

        // Highlight the submit button
        const submitButton = textarea.form?.querySelector('button[type="submit"]');
        if (submitButton) {
          submitButton.classList.add("ring-4", "ring-green-300", "ring-opacity-50");
          setTimeout(() => {
            submitButton.classList.remove("ring-4", "ring-green-300", "ring-opacity-50");
          }, 2000);
        }
      }
    }

    async function fetchReports() {
      setIsFetchingReports(true);
      try {
        const response = await api.get("user-latest-report/");
        if (response.status === 200) {
          setLatestFullReports(response.data);
        }
      } catch (error) {
        console.error("Error fetching reports:", error);
      } finally {
        setIsFetchingReports(false);
      }
    }

    fetchReports();
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setMessage("");
    setShowRechargeLink(false);
    setIsSubmitting(true);

    const timeoutId = setTimeout(() => {
      alert("Submission is taking longer than expected. Please try again.");
      setIsSubmitting(false);
    }, 10000);

    const confirmReport = window.confirm("This will deduct one report credit. Are you sure you want to continue?");
    if (!confirmReport) {
      clearTimeout(timeoutId);
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await api.post("create-report/", {
        business_idea_input: businessIdea,
        language,
      });
      clearTimeout(timeoutId);

      if (response.status === 200 || response.status === 201) {
        window.location.href = `/full-report/${response.data.report_id}`;
      }
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.response) {
        const errorMessage = error.response.data.message;
        setMessage(errorMessage || "An error occurred. Please try again.");
        if (errorMessage.includes("Not enough credits")) {
          setShowRechargeLink(true);
        }
      } else {
        setMessage("An unexpected error occurred.");
      }
      console.error("Failed to submit form:", error.response?.data);
    } finally {
      setIsSubmitting(false);
    }
  }

  const openModal = (report) => {
    setSelectedReport(report);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedReport(null);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Simple Header - Matching the dashboard page */}
      {/* <header className="h-16 border-b border-gray-100 flex items-center px-8">
        <div className="max-w-6xl w-full mx-auto flex justify-between">
          <span className="font-medium text-xl text-green-600">FeedbackbyAI</span>
          <Link to="/dashboard/" className="text-gray-500 hover:text-gray-700">
            Back to Dashboard
          </Link>
        </div>
      </header> */}
      <EnhancedHeader />

      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Page Header with Icon - Matching the card style */}
        <div className="flex flex-col items-center justify-center mb-12">
          <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mb-6">
            <FileText className="w-10 h-10 text-green-600" />
          </div>
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-3xl font-bold text-gray-900 text-center mb-2"
          >
            Generate Business Plan
          </motion.h1>
          <p className="text-center text-gray-600 max-w-2xl mx-auto">
            Get a comprehensive 100+ page analysis to validate and refine your business concept.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 mb-12">
          {message && (
            <div
              className={`mb-6 p-4 rounded-lg ${
                showRechargeLink ? "bg-yellow-50 border-l-4 border-yellow-400" : "bg-red-50 border-l-4 border-red-400"
              }`}
            >
              <p className="text-sm font-medium text-gray-800">{message}</p>
              {showRechargeLink && (
                <Link to="/credits/" className="inline-block mt-2 text-green-600 hover:text-green-800 font-semibold">
                  Recharge credits →
                </Link>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="businessIdea" className="block text-sm font-medium text-gray-700 mb-2">
                Describe your business idea in detail
              </label>
              <textarea
                id="businessIdea"
                value={businessIdea}
                onChange={(e) => setBusinessIdea(e.target.value)}
                placeholder="Detail your business concept, including unique features, target market, pricing strategy, and potential challenges. The more comprehensive your description, the more tailored our analysis will be."
                rows="7"
                className="w-full p-4 text-gray-700 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                required
              />
            </div>

            <div>
              <label htmlFor="language" className="block text-sm font-medium text-gray-700 mb-2">
                Select Report Language
              </label>
              <div className="relative">
                <select
                  id="language"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="block w-full pl-3 pr-10 py-3 text-base border-gray-300 focus:ring-green-500 focus:border-green-500 rounded-lg appearance-none bg-white"
                >
                  <option value="english">English</option>
                  <option value="hindi">Hindi</option>
                  <option value="spanish">Spanish</option>
                  <option value="french">French</option>
                  <option value="arabic">Arabic</option>
                  <option value="russian">Russian</option>
                  <option value="portuguese">Portuguese</option>
                  <option value="indonesian">Indonesian</option>
                  <option value="german">German</option>
                  <option value="japanese">Japanese</option>
                  <option value="swahili">Swahili</option>
                  <option value="turkish">Turkish</option>
                  <option value="korean">Korean</option>
                  <option value="italian">Italian</option>
                  <option value="vietnamese">Vietnamese</option>
                  <option value="polish">Polish</option>
                  <option value="ukrainian">Ukrainian</option>
                  <option value="dutch">Dutch</option>
                  <option value="greek">Greek</option>
                  <option value="hebrew">Hebrew</option>
                  <option value="thai">Thai</option>
                  <option value="persian">Persian</option>
                  <option value="czech">Czech</option>
                  <option value="swedish">Swedish</option>
                  <option value="romanian">Romanian</option>
                  <option value="hungarian">Hungarian</option>
                  <option value="danish">Danish</option>
                  <option value="finnish">Finnish</option>
                  <option value="slovak">Slovak</option>
                  <option value="norwegian">Norwegian</option>
                  <option value="serbian">Serbian</option>
                  <option value="bulgarian">Bulgarian</option>
                  <option value="mandarin">Mandarin</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <ChevronDown className="h-4 w-4" />
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full bg-green-600 hover:bg-green-700 text-white text-lg font-semibold py-3 rounded-lg
                ${isSubmitting ? "opacity-75 cursor-not-allowed" : ""}
                focus:outline-none transition-all duration-300`}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Generating Your Report...
                </span>
              ) : (
                "Generate My Business Plan"
              )}
            </button>
          </form>
        </div>

        {/* Feature highlights that match the card style */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-gray-50 rounded-xl p-6">
            <div className="flex items-center mb-3">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                <FileText className="w-4 h-4 text-green-600" />
              </div>
              <h3 className="font-medium text-gray-900">100+ Page Report</h3>
            </div>
            <p className="text-sm text-gray-600">Comprehensive business analysis with actionable insights for launch.</p>
          </div>

          <div className="bg-gray-50 rounded-xl p-6">
            <div className="flex items-center mb-3">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                <Target className="w-4 h-4 text-blue-600" />
              </div>
              <h3 className="font-medium text-gray-900">Market Analysis</h3>
            </div>
            <p className="text-sm text-gray-600">In-depth research on your target market, customers, and competitors.</p>
          </div>

          <div className="bg-gray-50 rounded-xl p-6">
            <div className="flex items-center mb-3">
              <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                <TrendingUp className="w-4 h-4 text-purple-600" />
              </div>
              <h3 className="font-medium text-gray-900">Growth Strategy</h3>
            </div>
            <p className="text-sm text-gray-600">Actionable recommendations for marketing, sales, and scaling.</p>
          </div>
        </div>

        {/* Previous Reports Section */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
          <h2 className="text-xl font-bold text-gray-800 mb-6">My Previous Business Plans</h2>
          <p className="text-sm text-gray-600 mb-4">
            *Opening a new report deducts report credits automatically. Select the correct report if you have multiple reports.
          </p>
          {isFetchingReports ? (
            <div className="flex justify-center items-center h-24">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-500"></div>
            </div>
          ) : (
            <ul className="space-y-4">
              {latestFullReports.length > 0 ? (
                latestFullReports.map((report) => (
                  <li key={report.report_id} className="border-b border-gray-200 pb-4">
                    <Link to={`/full-report/${report.report_id}`} className="text-green-600 hover:text-green-800 font-medium">
                      {report.input.substring(0, 100) + (report.input.length > 100 ? "..." : "")}
                    </Link>
                    {report.input.length > 100 && (
                      <button onClick={() => openModal(report)} className="ml-4 text-sm text-gray-500 hover:text-gray-700">
                        View More
                      </button>
                    )}
                  </li>
                ))
              ) : (
                <p className="text-gray-600">No previous reports found. Generate your first business plan now!</p>
              )}
            </ul>
          )}
        </div>
      </main>

      <CustomModal isOpen={isModalOpen} onRequestClose={closeModal} content={selectedReport?.input} />
    </div>
  );
}
// ReportDashboard.jsx

// import React, { useState, useEffect, useRef } from "react";
// import { Link, useLocation } from "react-router-dom";
// import { motion, AnimatePresence } from "framer-motion";
// import api from "./axiosConfig";
// import Modal from "react-modal"; // Ensure react-modal is imported
// import { formatDistanceToNow } from "date-fns";
// import {
//   ChevronDown,
//   Sparkles,
//   Eye, // Icon for View Input button
//   // MessageSquare, // Removed if not used
//   // Clock,        // Removed if not used
//   Target,
//   // Gift,        // Removed if not used
//   // Star,        // Removed if not used
//   ChevronRight,
//   X,
//   ExternalLink,
//   // Megaphone,   // Removed if not used
//   // Activity,    // Removed if not used
//   // Users,       // Removed if not used
//   Calendar,
//   // Building,    // Removed if not used
//   TrendingUp,
//   // Mail,        // Removed if not used
//   CheckCircle,
//   // Search,      // Removed if not used
//   ArrowRight,
//   // Zap,         // Removed if not used
//   // Shield,      // Removed if not used
//   FileText,
//   Copy,
//   Loader2,
//   AlertCircle,
//   Info as InfoIcon,
// } from "lucide-react";

// // --- Polished CustomModal Component ---
// // NOTE: Ensure Modal.setAppElement('#yourAppElementId') is called somewhere
// // in your application's root setup (e.g., index.js or App.js) for accessibility.
// const CustomModal = ({ isOpen, onRequestClose, content }) => {
//   const [isCopied, setIsCopied] = useState(false);

//   const handleCopy = async () => {
//     if (!content) return;
//     try {
//       await navigator.clipboard.writeText(content);
//       setIsCopied(true);
//       setTimeout(() => setIsCopied(false), 2500);
//     } catch (err) {
//       console.error("Failed to copy text: ", err);
//     }
//   };

//   return (
//     <Modal
//       isOpen={isOpen} // This prop controls visibility
//       onRequestClose={onRequestClose} // Function to call when closing (overlay click, Esc key)
//       shouldCloseOnOverlayClick={true}
//       shouldCloseOnEsc={true}
//       className="fixed inset-0 z-50 flex items-center justify-center p-4"
//       overlayClassName="fixed inset-0 bg-black bg-opacity-70 z-40 backdrop-blur-sm"
//       contentLabel="Full Report Input Text" // Accessibility label
//     >
//       {/* AnimatePresence helps with exit animations */}
//       <AnimatePresence>
//         {/* Conditional rendering based on isOpen is crucial for AnimatePresence */}
//         {isOpen && (
//           <motion.div
//             key="modalContent" // Added key for AnimatePresence
//             initial={{ opacity: 0, scale: 0.9 }}
//             animate={{ opacity: 1, scale: 1 }}
//             exit={{ opacity: 0, scale: 0.9 }}
//             transition={{ duration: 0.2, ease: "easeOut" }}
//             className="bg-white rounded-2xl shadow-xl max-w-2xl w-full mx-auto overflow-hidden"
//             role="dialog"
//             aria-modal="true"
//           >
//             {/* Modal Header */}
//             <div className="flex justify-between items-center p-5 sm:p-6 border-b border-gray-200">
//               <h3 className="text-lg font-semibold text-gray-800">Full Input Text</h3>
//               <button
//                 onClick={onRequestClose} // Ensure this closes the modal
//                 className="p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
//                 aria-label="Close modal"
//               >
//                 <X size={20} />
//               </button>
//             </div>

//             {/* Modal Content */}
//             <div className="p-5 sm:p-6 max-h-[60vh] overflow-y-auto bg-slate-50">
//               {/* Display the content passed via props */}
//               <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{content || "No content available."}</p>
//             </div>

//             {/* Modal Footer */}
//             <div className="flex flex-col sm:flex-row justify-end items-center space-y-2 sm:space-y-0 sm:space-x-3 p-5 sm:p-6 bg-gray-50 border-t border-gray-200">
//               <button
//                 onClick={handleCopy}
//                 disabled={!content}
//                 className={`w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 ${
//                   isCopied
//                     ? "bg-green-100 text-green-700 ring-green-500 cursor-default"
//                     : "bg-emerald-600 hover:bg-emerald-700 text-white ring-emerald-500 disabled:bg-gray-300 disabled:cursor-not-allowed"
//                 }`}
//               >
//                 {isCopied ? (
//                   <>
//                     {" "}
//                     <CheckCircle size={16} className="mr-1.5" /> Copied!
//                   </>
//                 ) : (
//                   <>
//                     {" "}
//                     <Copy size={16} className="mr-1.5" /> Copy Text{" "}
//                   </>
//                 )}
//               </button>
//               <button
//                 onClick={onRequestClose} // Ensure this closes the modal
//                 className="w-full sm:w-auto px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
//               >
//                 Close
//               </button>
//             </div>
//           </motion.div>
//         )}
//       </AnimatePresence>
//     </Modal>
//   );
// };

// // --- Helper function for safe date formatting ---
// const formatRelativeDate = (dateString) => {
//   if (!dateString) return "N/A";
//   try {
//     const date = new Date(dateString);
//     if (isNaN(date.getTime())) {
//       console.warn("Invalid date string received:", dateString);
//       return "Invalid Date";
//     }
//     return formatDistanceToNow(date, { addSuffix: true });
//   } catch (error) {
//     console.error("Error formatting date:", dateString, error);
//     return "Error";
//   }
// };

// // --- ReportDashboard Component ---
// export default function ReportDashboard() {
//   // --- State Variables ---
//   const [businessIdea, setBusinessIdea] = useState("");
//   const [language, setLanguage] = useState("english");
//   const [isSubmitting, setIsSubmitting] = useState(false);
//   const [latestFullReports, setLatestFullReports] = useState([]);
//   const [isFetchingReports, setIsFetchingReports] = useState(true);
//   const [message, setMessage] = useState("");
//   const [messageType, setMessageType] = useState("error");
//   const [showRechargeLink, setShowRechargeLink] = useState(false);
//   // ** Crucial State for the Modal **
//   const [isModalOpen, setIsModalOpen] = useState(false); // Controls if modal is open/closed
//   const [selectedReportInput, setSelectedReportInput] = useState(""); // Holds the text for the modal

//   const formRef = useRef(null);
//   const location = useLocation(); // Keep if used elsewhere, otherwise remove

//   // --- useEffect for localStorage and fetching reports ---
//   // (Keep as previously corrected)
//   useEffect(() => {
//     const storedInput = localStorage.getItem("businessPlanInput");
//     if (storedInput) {
//       setBusinessIdea(storedInput);
//       localStorage.removeItem("businessPlanInput");
//       const textarea = document.getElementById("businessIdea");
//       if (textarea) {
//         setTimeout(() => {
//           textarea.scrollIntoView({ behavior: "smooth", block: "center" });
//           textarea.focus({ preventScroll: true });
//           const submitButton = formRef.current?.querySelector('button[type="submit"]');
//           if (submitButton) {
//             submitButton.classList.add("ring-4", "ring-emerald-300", "ring-opacity-70", "transition-all", "duration-1000", "ease-out");
//             setTimeout(() => {
//               submitButton.classList.remove("ring-4", "ring-emerald-300", "ring-opacity-70");
//             }, 2500);
//           }
//         }, 100);
//       }
//     }

//     async function fetchReports() {
//       setIsFetchingReports(true);
//       try {
//         const response = await api.get("user-latest-report/");
//         // ** Check if report data includes the 'input' field **
//         console.log("Fetched reports data:", response.data); // DEBUG: Check fetched data structure
//         if (response.status === 200 && Array.isArray(response.data)) {
//           setLatestFullReports(response.data);
//         } else {
//           setLatestFullReports([]);
//         }
//       } catch (error) {
//         console.error("Error fetching reports:", error);
//         setLatestFullReports([]);
//       } finally {
//         setIsFetchingReports(false);
//       }
//     }
//     fetchReports();
//   }, []);

//   // --- Form Submission Handler ---
//   // (Keep as previously corrected)
//   async function handleSubmit(e) {
//     e.preventDefault();
//     setMessage("");
//     setShowRechargeLink(false);
//     if (!businessIdea.trim()) {
//       setMessage("Please describe your business idea before generating the plan.");
//       setMessageType("error");
//       return;
//     }
//     const confirmReport = window.confirm("Generating a new business plan will use one Report Credit. Are you sure you want to proceed?");
//     if (!confirmReport) return;
//     setIsSubmitting(true);
//     try {
//       const response = await api.post("create-report/", { business_idea_input: businessIdea, language });
//       if ((response.status === 200 || response.status === 201) && response.data.report_id) {
//         window.location.href = `/full-report/${response.data.report_id}`;
//       } else {
//         throw new Error(response.data?.message || "Report generation initiated but no ID received.");
//       }
//     } catch (error) {
//       console.error("Failed to submit form:", error);
//       let specificMessage = "An unexpected error occurred while generating the report. Please try again later.";
//       let type = "error";
//       setShowRechargeLink(false);
//       if (error.response) {
//         specificMessage = error.response.data?.detail || error.response.data?.message || specificMessage;
//         if (specificMessage.toLowerCase().includes("not enough credits") || error.response.status === 402) {
//           specificMessage = "You don't have enough Report Credits to generate a new plan.";
//           setShowRechargeLink(true);
//           type = "info";
//         }
//       } else if (error.request) {
//         specificMessage = "Could not connect to the server. Please check your internet connection.";
//       } else {
//         specificMessage = error.message || specificMessage;
//       }
//       setMessage(specificMessage);
//       setMessageType(type);
//     } finally {
//       setIsSubmitting(false);
//     }
//   }

//   // --- ** Modal Control Functions - Ensure these are correct ** ---
//   const openInputModal = (inputContent) => {
//     console.log("Opening modal with content:", inputContent); // DEBUG: Check content
//     setSelectedReportInput(inputContent || ""); // Set content, ensure it's not undefined
//     setIsModalOpen(true); // Set state to open the modal
//   };

//   const closeInputModal = () => {
//     setIsModalOpen(false); // Set state to close the modal
//     // Optional: Delay clearing content slightly if using exit animations
//     // setTimeout(() => setSelectedReportInput(""), 300); // Match animation duration
//     setSelectedReportInput(""); // Clear content immediately is usually fine
//   };

//   // --- Alert Component ---
//   const AlertMessage = ({ message, type = "error", showLink, linkTarget = "/credits/" }) => {
//     if (!message) return null;
//     const bgColor = type === "error" ? "bg-red-50" : type === "info" ? "bg-blue-50" : "bg-green-50";
//     const borderColor = type === "error" ? "border-red-300" : type === "info" ? "border-blue-300" : "border-green-300";
//     const textColor = type === "error" ? "text-red-800" : type === "info" ? "text-blue-800" : "text-green-800";
//     const iconColor = type === "error" ? "text-red-500" : type === "info" ? "text-blue-500" : "text-green-500";
//     const Icon = type === "error" ? AlertCircle : InfoIcon;
//     return (
//       <div className={`mb-6 p-4 rounded-lg border ${bgColor} ${borderColor} shadow-sm`}>
//         {" "}
//         <div className="flex items-start">
//           {" "}
//           <div className="flex-shrink-0">
//             {" "}
//             <Icon className={`h-5 w-5 ${iconColor}`} aria-hidden="true" />{" "}
//           </div>{" "}
//           <div className="ml-3 flex-1 md:flex md:justify-between">
//             {" "}
//             <p className={`text-sm ${textColor}`}>{message}</p>{" "}
//             {showLink && (
//               <p className="mt-2 text-sm md:mt-0 md:ml-6">
//                 {" "}
//                 <Link to={linkTarget} className={`whitespace-nowrap font-medium ${textColor} hover:opacity-80`}>
//                   {" "}
//                   Get Credits <span aria-hidden="true">&rarr;</span>{" "}
//                 </Link>{" "}
//               </p>
//             )}{" "}
//           </div>{" "}
//         </div>{" "}
//       </div>
//     );
//   };

//   // --- Render Logic ---
//   return (
//     <div className="min-h-screen bg-slate-50">
//       {/* Header */}
//       <header className="h-16 border-b border-gray-100 bg-white shadow-sm flex items-center px-4 sm:px-6 lg:px-8 sticky top-0 z-30">
//         {/* ... header content ... */}
//         <div className="max-w-6xl w-full mx-auto flex justify-between items-center">
//           <span className="font-semibold text-lg text-emerald-600 tracking-tight">FeedbackbyAI</span>
//           <Link to="/dashboard/" className="text-sm text-gray-600 hover:text-emerald-600 transition-colors flex items-center">
//             Back to Dashboard
//           </Link>
//         </div>
//       </header>

//       {/* Main Content */}
//       <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
//         {/* Page Header */}
//         {/* ... page header content ... */}
//         <div className="text-center mb-10 sm:mb-14">
//           {" "}
//           <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-full mb-5 ring-4 ring-emerald-50">
//             {" "}
//             <FileText className="w-8 h-8 text-emerald-600" />{" "}
//           </div>{" "}
//           <motion.h1
//             initial={{ opacity: 0, y: -10 }}
//             animate={{ opacity: 1, y: 0 }}
//             transition={{ duration: 0.4, ease: "easeOut" }}
//             className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3 tracking-tight"
//           >
//             {" "}
//             Generate Business Plan{" "}
//           </motion.h1>{" "}
//           <p className="text-base sm:text-lg text-gray-600 max-w-xl mx-auto">
//             {" "}
//             Get a comprehensive 100+ page analysis to validate and refine your business concept.
//           </p>{" "}
//         </div>

//         {/* Form Section Card */}
//         <div ref={formRef} className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sm:p-8 md:p-10 mb-12 sm:mb-16">
//           {/* ... Form elements ... */}
//           <AlertMessage message={message} type={messageType} showLink={showRechargeLink} />
//           <form onSubmit={handleSubmit} className="space-y-6">
//             {/* Business Idea Textarea */}
//             <div>
//               {" "}
//               <label htmlFor="businessIdea" className="block text-sm font-semibold text-gray-700 mb-2">
//                 Describe your business idea in detail
//               </label>{" "}
//               <textarea
//                 id="businessIdea"
//                 value={businessIdea}
//                 onChange={(e) => setBusinessIdea(e.target.value)}
//                 placeholder="Detail your business concept, including unique features, target market, pricing strategy, and potential challenges. The more comprehensive your description, the more tailored our analysis will be.."
//                 rows="8"
//                 className="w-full p-4 text-gray-800 bg-slate-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 focus:bg-white transition-all duration-200 ease-in-out placeholder-gray-400 text-base leading-relaxed shadow-sm"
//                 required
//               />{" "}
//               <p className="mt-1.5 text-xs text-gray-500">Be as detailed as possible for the best results.</p>{" "}
//             </div>
//             {/* Language Select */}
//             <div>
//               {" "}
//               <label htmlFor="language" className="block text-sm font-semibold text-gray-700 mb-2">
//                 Report Language
//               </label>{" "}
//               <div className="relative">
//                 {" "}
//                 <select
//                   id="language"
//                   value={language}
//                   onChange={(e) => setLanguage(e.target.value)}
//                   className="block w-full pl-4 pr-10 py-3 text-base border border-gray-300 bg-white rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 shadow-sm"
//                 >
//                   {" "}
//                   {/* Options */} <option value="english">English</option> {/* ... other options ... */}{" "}
//                   <option value="hindi">Hindi</option> <option value="spanish">Spanish</option> <option value="french">French</option>{" "}
//                   <option value="arabic">Arabic</option> <option value="russian">Russian</option>{" "}
//                   <option value="portuguese">Portuguese</option> <option value="indonesian">Indonesian</option>{" "}
//                   <option value="german">German</option> <option value="japanese">Japanese</option> <option value="swahili">Swahili</option>{" "}
//                   <option value="turkish">Turkish</option> <option value="korean">Korean</option> <option value="italian">Italian</option>{" "}
//                   <option value="vietnamese">Vietnamese</option> <option value="polish">Polish</option>{" "}
//                   <option value="ukrainian">Ukrainian</option> <option value="dutch">Dutch</option> <option value="greek">Greek</option>{" "}
//                   <option value="hebrew">Hebrew</option> <option value="thai">Thai</option> <option value="persian">Persian</option>{" "}
//                   <option value="czech">Czech</option> <option value="swedish">Swedish</option> <option value="romanian">Romanian</option>{" "}
//                   <option value="hungarian">Hungarian</option> <option value="danish">Danish</option>{" "}
//                   <option value="finnish">Finnish</option> <option value="slovak">Slovak</option>{" "}
//                   <option value="norwegian">Norwegian</option> <option value="serbian">Serbian</option>{" "}
//                   <option value="bulgarian">Bulgarian</option> <option value="mandarin">Mandarin</option>{" "}
//                 </select>{" "}
//                 <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500">
//                   <ChevronDown className="h-5 w-5" />
//                 </div>{" "}
//               </div>{" "}
//             </div>
//             {/* Submit Button */}
//             <button
//               type="submit"
//               disabled={isSubmitting}
//               className={`w-full inline-flex items-center justify-center bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-base font-semibold py-3.5 px-6 rounded-xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 shadow-md disabled:opacity-60 disabled:cursor-not-allowed group`}
//             >
//               {" "}
//               {isSubmitting ? (
//                 <>
//                   {" "}
//                   <Loader2 className="animate-spin mr-2 h-5 w-5" /> Generating Your Plan...
//                 </>
//               ) : (
//                 <>
//                   {" "}
//                   <Sparkles size={18} className="mr-2 group-hover:scale-110 transition-transform" /> Generate My Business Plan
//                 </>
//               )}{" "}
//             </button>
//             {/* <p className="text-center text-xs text-gray-500">Generates a 100+ page report. Requires 1 Report Credit.</p> */}
//           </form>
//         </div>

//         {/* Feature highlights */}
//         {/* ... feature highlights content ... */}
//         <div className="mb-12 sm:mb-16">
//           {" "}
//           <h2 className="text-xl font-semibold text-gray-800 text-center mb-6 sm:mb-8">What You'll Get</h2>{" "}
//           <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6">
//             {" "}
//             {/* Feature Card */}{" "}
//             <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition-shadow duration-200">
//               {" "}
//               <div className="flex items-center mb-3">
//                 {" "}
//                 <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
//                   <FileText className="w-5 h-5 text-emerald-600" />
//                 </div>{" "}
//                 <h3 className="font-semibold text-gray-800 text-base leading-tight">Comprehensive Analysis</h3>{" "}
//               </div>{" "}
//               <p className="text-sm text-gray-600">100+ pages covering every angle of your business idea.</p>{" "}
//             </div>{" "}
//             {/* Feature Card */}{" "}
//             <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition-shadow duration-200">
//               {" "}
//               <div className="flex items-center mb-3">
//                 {" "}
//                 <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
//                   <Target className="w-5 h-5 text-blue-600" />
//                 </div>{" "}
//                 <h3 className="font-semibold text-gray-800 text-base leading-tight">Market &amp; Competitor Insights</h3>{" "}
//               </div>{" "}
//               <p className="text-sm text-gray-600">Deep dive into market size, customer segments, and competitive landscape.</p>{" "}
//             </div>{" "}
//             {/* Feature Card */}{" "}
//             <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition-shadow duration-200">
//               {" "}
//               <div className="flex items-center mb-3">
//                 {" "}
//                 <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
//                   <TrendingUp className="w-5 h-5 text-purple-600" />
//                 </div>{" "}
//                 <h3 className="font-semibold text-gray-800 text-base leading-tight">Strategic Roadmap</h3>{" "}
//               </div>{" "}
//               <p className="text-sm text-gray-600">Actionable marketing, sales, operations, and financial plans.</p>{" "}
//             </div>{" "}
//           </div>{" "}
//         </div>

//         {/* Previous Reports Section */}
//         <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sm:p-8">
//           <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-6">My Previous Business Plans</h2>
//           {isFetchingReports ? (
//             // Loading State
//             <div className="flex flex-col justify-center items-center py-10 text-center">
//               {" "}
//               <Loader2 className="h-8 w-8 text-emerald-500 animate-spin mb-3" />{" "}
//               <p className="text-gray-500 text-sm">Loading previous reports...</p>{" "}
//             </div>
//           ) : (
//             // List or Empty State
//             <div className="flow-root">
//               <ul role="list" className="-my-5 divide-y divide-gray-100">
//                 {latestFullReports.length > 0 ? (
//                   latestFullReports.map(
//                     (report) =>
//                       // ** Check if report.input exists before rendering the button **
//                       report && (
//                         <li key={report.report_id} className="py-5 group">
//                           <div className="relative focus-within:ring-2 focus-within:ring-emerald-500 rounded-lg p-1 -m-1">
//                             <h3 className="text-sm font-semibold text-gray-800 line-clamp-1 mb-1">
//                               <Link
//                                 to={`/full-report/${report.report_id}`}
//                                 className="hover:text-emerald-600 transition-colors focus:outline-none"
//                               >
//                                 <span className="absolute inset-0" aria-hidden="true" />
//                                 {/* Use report title or fallback to input */}
//                                 {report.title || `Report for: ${report.input ? report.input.substring(0, 60) + "..." : "Unknown Input"}`}
//                               </Link>
//                             </h3>
//                             {/* <p className="text-xs text-gray-500 flex items-center mb-2">
//                               <Calendar size={12} className="mr-1.5 text-gray-400" />
//                               Generated: {formatRelativeDate(report.created_at)}
//                             </p> */}
//                             <div className="flex items-center space-x-3 mt-2">
//                               {/* View Report Button */}
//                               <Link
//                                 to={`/full-report/${report.report_id}`}
//                                 className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200 hover:bg-emerald-100 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-emerald-500"
//                               >
//                                 {" "}
//                                 <ExternalLink size={12} className="mr-1" /> View Report{" "}
//                               </Link>
//                               {/* ** VIEW INPUT BUTTON - Ensure report.input exists ** */}
//                               {report.input && ( // Conditionally render button if input exists
//                                 <button
//                                   type="button" // Explicitly set type
//                                   onClick={(e) => {
//                                     e.stopPropagation(); // Prevent link navigation if button is clicked
//                                     openInputModal(report.input); // Call the function to open modal
//                                   }}
//                                   className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-full bg-gray-50 text-gray-600 ring-1 ring-inset ring-gray-200 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-500 z-10 relative" // Added z-index just in case
//                                 >
//                                   <Eye size={12} className="mr-1" /> View Input
//                                 </button>
//                               )}
//                             </div>
//                           </div>
//                         </li>
//                       )
//                   )
//                 ) : (
//                   // Empty State
//                   <div className="text-center py-10">
//                     {" "}
//                     <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
//                       {" "}
//                       <FileText className="h-6 w-6 text-gray-400" />{" "}
//                     </div>{" "}
//                     <p className="text-sm text-gray-600">No previous reports found.</p>{" "}
//                     <p className="text-xs text-gray-500 mt-1">Generate your first business plan using the form above.</p>{" "}
//                   </div>
//                 )}
//               </ul>
//             </div>
//           )}
//         </div>
//       </main>

//       {/* Modal Rendering - Ensure props are passed correctly */}
//       <CustomModal isOpen={isModalOpen} onRequestClose={closeInputModal} content={selectedReportInput} />

//       {/* Optional Footer */}
//       {/* <ReportFooter /> */}
//     </div>
//   );
// }
